import { useLogEvent } from 'hooks/useLogEvent';
import React, {
  FC,
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { sanitizePathname } from 'utils/pageTitle.utils';

interface ITrackNavigationEventsValues {
  currentPathname?: string | undefined;
  previousPathname: string | undefined;
}

const TrackNavigationEventsContext = createContext<ITrackNavigationEventsValues>({
  previousPathname: undefined,
});

const TrackNavigationEventsProvider: FC<PropsWithChildren> = ({ children }) => {
  const { logEvent } = useLogEvent();
  const { pathname } = useLocation();

  const [currentPathname, setCurrentPathname] = useState<string | undefined>();
  const [previousPathname, setPreviousPathname] = useState<string | undefined>();

  useEffect(() => {
    setCurrentPathname(pathname);
    try {
      // To have a generic path for all ids we replace our uuids with :id
      const from = sanitizePathname(currentPathname);
      const to = sanitizePathname(pathname);

      if (from === to) {
        return;
      }

      // Save old currentPathname as previous BEFORE updating current
      setPreviousPathname(currentPathname);

      logEvent('NAVIGATION', { from, to });
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <TrackNavigationEventsContext.Provider value={{ currentPathname, previousPathname }}>
      {children}
    </TrackNavigationEventsContext.Provider>
  );
};

const usePreviousPathname = () => {
  const { previousPathname, currentPathname } = useContext(TrackNavigationEventsContext);
  return { previousPathname, currentPathname };
};

export { TrackNavigationEventsProvider, usePreviousPathname };

export default TrackNavigationEventsContext;
